import { Component, Inject, OnInit, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { ExternalEventsHandlerService } from '../../../../../core/services/external-events-handler.service';
import { FormAlertType } from '../../../../../core/constants/constants';
import { ClipboardService} from 'ngx-clipboard';
import { PresenceMonitorState, PresenceMonitor } from '../../../../../core/interfaces/msteams-presence-data';
import { WorkflowService } from 'src/app/core/services/workflow.service';
import { MSTeamsSharedService } from 'src/app/core/services/msteams-shared.service';
import { NotifierService } from 'angular-notifier';
import { CopyTextDialogComponent } from 'src/app/shared/components/dialogs/copy-text-dialog/copy-text-dialog.component';
import { AdminService } from 'src/app/core/services/admin.service';

const logContext = 'PresenceMonitorConfigDialogComponent';

export enum MonitorOperation {
	NONE = 0,
	DELETE = 1,
	RENEW = 2
}

@Component({
  selector: 'app-presence-monitor-config-dialog',
  templateUrl: './presence-monitor-config-dialog.component.html',
  styleUrls: ['./presence-monitor-config-dialog.component.scss']
})
export class PresenceMonitorConfigDialogComponent implements OnInit, OnDestroy {
	configForm = this.formBuilder.group({
		monitorId: ['', [Validators.required, (control) => this.validateUniqueness(control, this)]],
		clientSecret: ['', [Validators.required]],
		deleteConfirm: ['', [Validators.required,
			Validators.pattern(new RegExp('^' + this.translateService.instant('deleteConfirmation.delete') + '$', 'i'))]],
		renewClientSecret: ['', [Validators.required]],
	});
	oAuthWindow:Window;
    private oAuthUrl:string;

	private tenantId: string = '';
	private clientId: string = '';
	private clientSecret: string = '';
	private connectionData: any = {};
	private msGraphConnectionData: any = {};
	private subscription: Subscription;
	private monitorList: PresenceMonitor[] = [];
	private monitorListChanged: boolean = false;
	private urlToCopy: string;
	private clUsers: number = undefined;
	private pmUsers: number = undefined;
	private subscribedUsers: number = 0;
	private intervalId: number;  
    private isDeactivated: boolean;
	private operationInProgress: MonitorOperation = MonitorOperation.NONE;
	private consentReceived: boolean = false;
	private mismatchError: boolean = false;
	private msTeamsEnabled: boolean = false;
	
	showFormError: boolean = false;
	formErrorMsg: string = "";

	showAddError: boolean = false;
	addErrorMsg: string = "";
	alertMsgStyle: any = { 'max-width': '950px' };
	alertType: string = FormAlertType.SUCCESS;

	showConsentStatus: boolean = false;
	consentMsg: string = "";
	consentStatusType: string = FormAlertType.SUCCESS;
	monitorOperationInProgress: any;
	
	showMonitorError: boolean;
	monitorErrorMsg: any;
	isEnglishLanguage: boolean = true; // Spacing looks bad for english and does not work for other languages
										// using this for now. Expect big changes during an angular upgrade so I don't want to waste time now on it
	constructor( @Inject(MAT_DIALOG_DATA) public data: any,
				public dialogRef: MatDialogRef<PresenceMonitorConfigDialogComponent>,
				public dialog: MatDialog,
				private translateService: TranslateService,
				private domSanitizer: DomSanitizer,
				private matIconRegistry: MatIconRegistry,
				private formBuilder: FormBuilder,
				private extEventHandlerService: ExternalEventsHandlerService,
				private clipboardService: ClipboardService,
				private workflowService: WorkflowService,
				private adminService: AdminService,
				private msteamsSharedService: MSTeamsSharedService,
				private notifier: NotifierService,
				private spinnerService: SpinnerService ) 
	{
		this.matIconRegistry.addSvgIcon(
			'trash_red',
			this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/images/trash_red.svg")
		);

		this.matIconRegistry.addSvgIcon(
			'external',
			this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/images/external.svg")
		);

		this.matIconRegistry.addSvgIcon(
			'external_disabled',
			this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/images/external_disabled.svg")
		);
	}

	async ngOnInit() 
	{
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
		this.spinnerService.showSpinner();
		this.isEnglishLanguage = (this.translateService.currentLang == 'en' || this.translateService.currentLang == 'en-GB' ? true : false);

		let product = this.data.product;
		
		try
		{
			await product.service.setupAllPresenceWorkflows(this.data.account);
		}
		catch (err)
		{
			this.displayErrorBasedOnException( err, false );
		}

		await product.service.getMSGraphConnectorData(this.data.account).then( connectorData => {
			this.tenantId = connectorData.tenantId;
			this.clientId = connectorData.clientId;
			this.connectionData.tenantId = this.tenantId;
			this.connectionData.clientId = this.clientId;
			this.msGraphConnectionData = this.connectionData;
		})
		.catch (err => {
			this.displayErrorBasedOnException( err, false );
		})

		try
		{
			// TBD: Should we cache the tags for the life of the dialog?
			// more investigation needed because this dialog also indirectly updates the tags
			this.msTeamsEnabled = false;
			let tags = await this.adminService.getAccountTags(this.data.account.accountId);
			if ( tags && tags.MSTeamsToCloudLink && tags.MSTeamsToCloudLink.enabled == true )
				this.msTeamsEnabled = true;
		}
		catch (err)  
		{
			this.displayErrorBasedOnException( err, false );
		}

		// if the tags were cached, they could be passed here
		// no waiting for this one to complete
		product.service.invokeWorkflowToRefreshSubscription(this.data.account);

		// this will get all the data from the workflow micro api with its state
		try
		{
			let monitorData = await product.service.getAllMonitorData(this.data.account, this.msGraphConnectionData);
			if ( monitorData != null )
			{
				this.monitorList = monitorData;
				this.determineMismatchError();
			}
		}
		catch (err) 
		{
			this.displayErrorBasedOnException( err, false );
		}
			
		this.subscription = this.extEventHandlerService.MSTeamsMonitorConsentReceived.subscribe( event => {
			console.debug(`${logContext}.getAccountMonitorDetails Received MONITOR CONSENT event`, event);
			this.consentReceived = true;
			this.handleConsentMessage(event);
		});

		await this.updateDataAndStatusUsingWorkFlow();
		this.spinnerService.hideSpinner();
	}

	async updateDataAndStatusUsingWorkFlow(reset: boolean = false): Promise<void>
	{
		try
		{
			let product = this.data.product;
			let validateData = await product.service.getAccountMonitorDetails(this.data.account, reset);
			console.debug(`${logContext}.getAccountMonitorDetails returned`, validateData);
			this.clUsers = validateData.cloudlinkUserCount;
			this.pmUsers = validateData.requiredConnectorIds;
			this.subscribedUsers = 0;

			// still does not give the app consistent status
			// do not trusting removing connectors using this interface
			// commenting out until I get a status better than I have already
			// if ( validateData.removedConnectors && validateData.removedConnectors.length > 0 )
			// {
			// 	validateData.removedConnectors.forEach(element => {
			// 		let ndx = this.monitorList.findIndex(m => m.connectionId == element.connectionId);
			// 		if ( ndx >= 0 )
			// 			this.monitorList.splice( ndx, 1 );
			// 	});
			// }

			if ( validateData.connectorIdStatus && validateData.connectorIdStatus.length > 0 )
			{
				// only trust the Authorized and subscribed states for now
				validateData.connectorIdStatus.forEach(element => {
					let ndx = this.monitorList.findIndex(m => m.connectionId == element.connectionId);
					if ( ndx >= 0 )
					{
						// do not trust any other status at this point - actually I don't event trust this status
						// this status does not consider the change in parent connector application so do not update 
						// if the monitor has a "NEEDS_UPDATE" state
						if ( this.monitorList[ndx].status != PresenceMonitorState.NEEDS_UPDATE )
						{
							if (element.status == PresenceMonitorState.AUTHORIZED )  
								this.monitorList[ndx].status = element.status;
							else if (element.status == PresenceMonitorState.SUBSCRIBED )
							{
								this.monitorList[ndx].status = element.status;
								this.monitorList[ndx].userCount = element.userCount;
								if ( element.userCount )
									this.subscribedUsers += element.userCount;
							}
						}
					}
					else
						console.debug(`${logContext}.getAccountMonitorDetails Could not find the connectionId ${element.connectionId} in the monitor list` );
				});
			}

			this.validateMonitorNumbers();
		}
		catch( err )
		{
			this.displayErrorBasedOnException( err, false );
		}
	}

	validateMonitorNumbers()
	{
		this.showMonitorError = false;
		if ( this.pmUsers > this.monitorList.length )
		{
			// display the need more monitors error
			this.monitorErrorMsg = this.translateService.instant("integrations.MSTeamsToCLPresence.error-more-monitors");
			this.alertType = FormAlertType.ERROR;
			this.showMonitorError = true;
		}
		else
		{
			// count the number of authorized monitors
			let count = 0;
			for ( let i = 0; i < this.monitorList.length; i++ )
			{
				if ( (this.monitorList[i].status == PresenceMonitorState.AUTHORIZED) || (this.monitorList[i].status == PresenceMonitorState.SUBSCRIBED) )
					count++
			}
			if ( count < this.pmUsers )
			{
				// not enough authorized
				this.monitorErrorMsg = this.translateService.instant("integrations.MSTeamsToCLPresence.error-authorize-monitors");
				this.alertType = FormAlertType.ERROR;
				this.showMonitorError = true;
			}
		}
	}

	validateUniqueness(control, self) {  
		if (control.value != "" && self.monitorList.length > 0) {
			for (let i = 0; i < self.monitorList.length; i++) {
				if (self.monitorList[i].monitorId.toLowerCase() == control.value.trim().toLowerCase()) {
					return { 'not-unique': true };
				}
			}
		}
		return null;
	}

	determineMismatchError()
	{
		for (let i = 0; i < this.monitorList.length; i++) 
		{
			if ( this.monitorList[i].status == PresenceMonitorState.NEEDS_UPDATE )
			{
				this.mismatchError = true;
				return;
			}
		}
		this.mismatchError = false;
	}

	disableAddButtons(): boolean
	{
		let disableButtons = false;
		if ( this.configForm == undefined || 
			this.configForm.controls.monitorId.value == "" || this.configForm.controls.monitorId.value == null ||
			this.configForm.controls.clientSecret.value == "" || this.configForm.controls.clientSecret.value == null ||
			this.configForm.controls.monitorId.valid == false) 
			disableButtons = true;
		return disableButtons;
	}

	onAuthorizeNow() 
	{
		this.clearAllErrorMessages();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
		this.spinnerService.showSpinner();
		return this.setupConnectorDataAndCreateConnector().then(created => {
			this.spinnerService.hideSpinner();
			// add the new connector to the monitor list
			this.monitorList.push( {
				monitorId: this.configForm.controls.monitorId.value.trim(), 
				connectionId: created.connectionId, 
				status: created.connected == true ? PresenceMonitorState.AUTHORIZED : PresenceMonitorState.NOT_AUTHORIZED});
			this.clearAddMonitorFields();
			this.monitorListChanged = true;
			this.determineMismatchError(); // don't know if this is really needed here
			this.validateMonitorNumbers();
			// the connector is not connected, so we will have to open the auth window
			if (created.connected == false && created._links && created._links['authenticationUrl']) 
			{           
				this.oAuthUrl = this.createAuthURL( created._links['authenticationUrl'], this.connectionData.monitorId );
				if(this.oAuthWindow) {
					this.oAuthWindow.close();
					this.oAuthWindow = null;
				}
				console.debug(`${logContext}.onAuthorizeNow open a window with URL=`, this.oAuthUrl);
				this.oAuthWindow = this.createOauthWindow(600, 600, 200, 200);
				this.oAuthWindow.location.href = this.oAuthUrl;
				this.consentReceived = false;
				this.intervalId = window.setInterval(this.checkOAuthWindow.bind(this), 250);
			}
		});
	}

	createAuthURL( baseUrl: string, monitorId: string ): string
	{
		return baseUrl + '&prompt=login&login_hint=' + monitorId;
	}

    private checkOAuthWindow(): void {
        
        if(this.isDeactivated && this.oAuthWindow){ 
            this.oAuthWindow.close();
        }

        if(!this.oAuthWindow || this.oAuthWindow.closed) {
            window.clearInterval(this.intervalId);
			if ( this.consentReceived == false )
				this.handleConsentMessage({});
			else
				this.consentReceived = false;
            // let redirect = this.getOAuth2RedirectUrl();
            // let error = this.translateService.instant('messages.errorUnknown');
            // redirect =`${redirect}?connected=false&integrationid=${this.integration.integrationId}&connectionid=${this.connection.connectionId}&error=${error}`;
            // window.location.href = redirect;
            return;
        }

		// still under investigation
		// copied from workflow but not sure I need it
        // try {
        //     console.log('OAUTH WINDOW LOCATION: ', this.oAuthWindow.location.href);

        //     if(this.oAuthWindow.location.href !== 'about:blank' && this.oAuthWindow.location.href !== this.oAuthUrl) {
        //         let redirect = this.getOAuth2RedirectUrl();
        //         if(this.oAuthWindow.location.href.match(redirect)) {
        //             log.info('Oauth Flow Complete: ', this.oAuthWindow.location.href);
        //             window.clearInterval(this.intervalId);
    
        //             this.activeModal.close(this.connection);
        //             this.alertSvc.showSuccess({
        //                 message: this.translateService.instant('messages.connectionCreated'),
        //                 enableClose: true,
        //                 duration: 3000
        //             });
        
        //             let redirect = this.oAuthWindow.location.href;
        //             if(this.oAuthWindow) {
        //                 this.oAuthWindow.close();
        //                 this.oAuthWindow = null;
        //             }
        //             window.location.href = redirect;
        //         } else {
        //             log.error('Oauth Flow Not Completed: ', this.oAuthWindow.location.href);
        //             let error = this.translateService.instant('messages.errorUnknown');
        //             redirect =`${redirect}?connected=false&integrationid=${this.integration.integrationId}&connectionid=${this.connection.connectionId}&error=${error}`;
        //         }           
        //     }
        // } catch (error) {
        //     if(!(error instanceof DOMException)){
        //         console.error('checkOAuthWindow: ', error);
        //     }
        // }
    }

	copyTheUrlAndShowNotification( baseUrlToCopy, monitorId )
	{
		if ( baseUrlToCopy && monitorId )
		{
			this.urlToCopy = this.createAuthURL( baseUrlToCopy, monitorId );
			setTimeout(() => {
				let wasCopied = this.clipboardService.copyFromContent(this.urlToCopy);
				if ( wasCopied )
				{
					this.notifier.notify('success', this.translateService.instant('integrations.MSTeamsToCLPresence.url-copied'));
					console.log(`${logContext}.copyTheUrlAndShowNotification copy of the url - success`, this.urlToCopy);
				}
				else
				{
					let dialogData = {
						instructionsStr: 'copy-text.copy-url-instruction',
						titleStr: 'copy-text.copy-url-title',
						copyStr: this.urlToCopy
					};
		
					let copyDialogRef = this.dialog.open(CopyTextDialogComponent, 
					{
						disableClose: true,
						autoFocus: false,
						data: dialogData
					});
					copyDialogRef.afterClosed().subscribe( secondCopied => {
						console.log("The result of the copy text dialog", secondCopied);
					
						if ( secondCopied == true )
						{
							this.notifier.notify('success', this.translateService.instant('integrations.MSTeamsToCLPresence.url-copied'));
							console.log(`${logContext}.copyTheUrlAndShowNotification copy of the url - success`, this.urlToCopy);
						}
						else
						{
							this.notifier.notify('error', this.translateService.instant('integrations.MSTeamsToCLPresence.url-not-copied'));
							console.error(`${logContext}.copyTheUrlAndShowNotification copy of the url failed`, this.urlToCopy);
						}
					});
				}
			}, 150);
		}
		else
		{
			this.urlToCopy = undefined;
			this.notifier.notify('error', this.translateService.instant('integrations.MSTeamsToCLPresence.no-url-error'));
			console.error(`${logContext}.copyTheUrlAndShowNotification copy of the url failed, there was no url to copy`, baseUrlToCopy );
		}
	}

	async onCopyUrl()
	{
		this.clearAllErrorMessages();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
		this.spinnerService.showSpinner();
		return this.setupConnectorDataAndCreateConnector().then(created => {
			// copy the url to the clipboard
			if (created._links && created._links['authenticationUrl'])
			{
				this.copyTheUrlAndShowNotification( created._links['authenticationUrl'], this.connectionData.monitorId );
			}
			else
			{
				this.copyTheUrlAndShowNotification( undefined, this.connectionData.monitorId );
			}
			// add the new connector to the monitor list
			this.monitorList.push( {
				monitorId: this.configForm.controls.monitorId.value.trim(), 
				connectionId: created.connectionId, 
				status: created.connected == true ? PresenceMonitorState.AUTHORIZED : PresenceMonitorState.NOT_AUTHORIZED});
			this.clearAddMonitorFields();
			this.monitorListChanged = true;
			this.determineMismatchError();
			this.validateMonitorNumbers();
			this.spinnerService.hideSpinner();
		})
		.catch( err => {
			this.displayErrorBasedOnException( err, true );
			this.spinnerService.hideSpinner();
		});
	}
	
	setupConnectorDataAndCreateConnector(): Promise<any>
	{
		let product = this.data.product;
		// get the two field values
		this.connectionData.connectionId = undefined;
		this.connectionData.clientSecret = this.configForm.controls.clientSecret.value?.trim();
		this.connectionData.monitorId = this.configForm.controls.monitorId.value?.trim();
		return product.service.saveConnectorData( this.data.account, this.connectionData ).then(connector => {
			return connector;
		})
		.catch( err => {
			this.displayErrorBasedOnException( err, true );
			this.spinnerService.hideSpinner();
			throw err;
		});
	}

	updateConnectorWithClientSecret(): Promise<any>
	{
		let product = this.data.product;
		// get the two field values
		this.connectionData.clientSecret = this.configForm.controls.renewClientSecret.value?.trim();
		this.connectionData.monitorId = this.monitorOperationInProgress.monitorId;
		this.connectionData.connectionId = this.monitorOperationInProgress.connectionId;

		return product.service.saveConnectorData( this.data.account, this.connectionData ).then(connector => {
			return connector;
		})
		.catch( err => {
			this.displayErrorBasedOnException( err, false );
			this.spinnerService.hideSpinner();
			throw err;
		});
	}

	async onDeleteMonitor(monitor)
	{
		this.operationInProgress = MonitorOperation.DELETE;
		this.monitorOperationInProgress = monitor;
		this.clearAllErrorMessages();
		this.validateMonitorNumbers();

		setTimeout(() => {
			let elem = document.getElementById("scrollToDelete")
			if ( elem && elem.scrollIntoView )
			{
				elem.scrollIntoView({
					behavior: "smooth",
					block: "center"
				});
			}
		}, 100);
	
	}

	onDeleteMonitorAfterConfirmation(monitor)
	{
		let product = this.data.product;
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
		this.spinnerService.showSpinner();
		return  product.service.deleteConnectorData( this.data.account, monitor.connectionId).then( () => {
			this.monitorListChanged = true;
			this.determineMismatchError();
			this.monitorList = this.monitorList.filter( m => m.monitorId != monitor.monitorId );
			this.clearConfirmDeleteControls();
			this.operationInProgress = MonitorOperation.NONE;
			this.monitorOperationInProgress = undefined;
			this.validateMonitorNumbers();
			this.spinnerService.hideSpinner();
		})
		.catch( err => {
			// this should not happen
			// delete is best effort handled by the service
			// only added this so that the spinner is removed 
			console.error(`${logContext}.onDeleteMonitorAfterConfirmation delete monitor should not throw an error=`, err);
			this.spinnerService.hideSpinner();
		});
	}

	operationInProgressForMyMonitor(monitor, operation)
	{
		return (this.operationInProgress == operation) && this.monitorOperationInProgress && this.monitorOperationInProgress.monitorId == monitor.monitorId;
	}
	
	onDeleteKeypress(event, monitor)
	{
		if (event.keyCode === 13) {
			if (this.configForm.controls.deleteConfirm && this.configForm.controls.deleteConfirm.dirty && this.configForm.controls.deleteConfirm.valid) {
				this.onDeleteMonitorAfterConfirmation(monitor);
			}
		}
	}

	onRetryMonitor(monitor)
	{
		let product = this.data.product;
		this.clearAllErrorMessages();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
		this.spinnerService.showSpinner();
		return product.service.getMicrosoftAuthConnectorWithLinks(monitor.connectionId).then(connectionData => {
			this.validateMonitorNumbers();
			this.spinnerService.hideSpinner();
			// the connector is not connected, so we will have to open the auth window
			if (connectionData.connected == false && connectionData._links && connectionData._links['authenticationUrl']) 
			{                    
				this.oAuthUrl = this.createAuthURL( connectionData._links['authenticationUrl'], monitor.monitorId );
				if(this.oAuthWindow) {
					this.oAuthWindow.close();
					this.oAuthWindow = null;
				}
				this.monitorListChanged = true;
				this.connectionData.monitorId = monitor.monitorId;
				console.debug(`${logContext}.onRetryMonitor open a window with URL=`, this.oAuthUrl);
				this.oAuthWindow = this.createOauthWindow(600, 600, 200, 200);
				this.oAuthWindow.location.href = this.oAuthUrl; 
				this.consentReceived = false;    
				this.intervalId = window.setInterval(this.checkOAuthWindow.bind(this), 100);
			}
		})
		.catch( err => {
			this.displayErrorBasedOnException( err, false );
			this.spinnerService.hideSpinner();
		});
	}

	onRenewMonitor(monitor)
	{
		this.operationInProgress = MonitorOperation.RENEW;
		this.monitorOperationInProgress = monitor;
		this.clearAllErrorMessages();

		setTimeout(() => {
			let elem = document.getElementById("scrollToRenew");
			if ( elem && elem.scrollIntoView )
			{
				elem.scrollIntoView({
					behavior: "smooth",
					block: "center"
				});
			}
		}, 100);
	}

	onRenewMonitorWithSecret(monitor)
	{
		this.clearAllErrorMessages();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
		this.spinnerService.showSpinner();
		return this.updateConnectorWithClientSecret().then(connector => {
			this.spinnerService.hideSpinner();
			// update the monitor's status in the monitor list
			let ndx = this.monitorList.findIndex(m => m.connectionId == connector.connectionId);
			if ( ndx >= 0 )
				this.monitorList[ndx].status = connector.connected == true ? PresenceMonitorState.AUTHORIZED : PresenceMonitorState.NOT_AUTHORIZED;

			this.onCancel(this.monitorOperationInProgress);
			this.monitorListChanged = true;
			this.determineMismatchError();
			this.validateMonitorNumbers();
			// the connector is not connected, so we will have to open the auth window
			if (connector.connected == false && connector._links && connector._links['authenticationUrl']) 
			{                    
				this.oAuthUrl = this.createAuthURL( connector._links['authenticationUrl'], monitor.monitorId );
				if(this.oAuthWindow) {
					this.oAuthWindow.close();
					this.oAuthWindow = null;
				}
				console.debug(`${logContext}.onRenewMonitorWithSecret open a window with URL=`, this.oAuthUrl);
				this.oAuthWindow = this.createOauthWindow(600, 600, 200, 200);
				this.oAuthWindow.location.href = this.oAuthUrl;
				this.consentReceived = false;
				this.intervalId = window.setInterval(this.checkOAuthWindow.bind(this), 200);
			}
		});
	}

	onRenewCopyUrlWithSecret(monitor)
	{
		this.clearAllErrorMessages();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
		this.spinnerService.showSpinner();
		return this.updateConnectorWithClientSecret().then(connector => {
			// copy the url to the clipboard
			if (connector._links && connector._links['authenticationUrl'])
			{
				this.copyTheUrlAndShowNotification( connector._links['authenticationUrl'], monitor.monitorId );
			}
			else
			{
				this.copyTheUrlAndShowNotification( undefined, monitor.monitorId );
			}

			// update the monitor's status in the monitor list
			let ndx = this.monitorList.findIndex(m => m.connectionId == connector.connectionId);
			if ( ndx >= 0 )
				this.monitorList[ndx].status = connector.connected == true ? PresenceMonitorState.AUTHORIZED : PresenceMonitorState.NOT_AUTHORIZED;

			this.onCancel(this.monitorOperationInProgress);
			this.monitorListChanged = true;
			this.determineMismatchError();
			this.validateMonitorNumbers();
			this.spinnerService.hideSpinner();
		})
		.catch( err => {
			this.displayErrorBasedOnException( err, false );
			this.spinnerService.hideSpinner();
		});
	}

	onRetryCopyUrl(monitor)
	{
		let product = this.data.product;
		this.clearAllErrorMessages();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
		this.spinnerService.showSpinner();
		return product.service.getMicrosoftAuthConnectorWithLinks(monitor.connectionId).then(connectionData => {
			if (connectionData.connected == false && connectionData._links && connectionData._links['authenticationUrl']) 
			{
				this.copyTheUrlAndShowNotification( connectionData._links['authenticationUrl'], monitor.monitorId );
			}
			else if (connectionData._links && connectionData._links['authenticationUrl']) // don't know if this makes sense or if it is possible
			{
				this.copyTheUrlAndShowNotification( connectionData._links['authenticationUrl'], monitor.monitorId );
			}
			else
			{
				this.copyTheUrlAndShowNotification( undefined, monitor.monitorId );
			}
			this.validateMonitorNumbers();
			this.spinnerService.hideSpinner();
		})
		.catch( err => {
			this.displayErrorBasedOnException( err, false );
			this.spinnerService.hideSpinner();
		});
	}

	onCancel(monitor)
	{
		this.operationInProgress = MonitorOperation.NONE;
		this.monitorOperationInProgress = undefined;
		this.clearConfirmDeleteControls();
		this.clearRenewControls();
	}

	clearConfirmDeleteControls()
	{
		this.configForm.controls.deleteConfirm.setValue("");
		this.configForm.controls.deleteConfirm.markAsUntouched();
	}

	clearRenewControls()
	{
		this.configForm.controls.renewClientSecret.setValue("");
		this.configForm.controls.renewClientSecret.markAsUntouched();
	}

	clearAddMonitorFields()
	{
		this.configForm.controls.monitorId.setValue("");
		this.configForm.controls.clientSecret.setValue("");
		this.configForm.controls.monitorId.markAsUntouched();
		this.configForm.controls.clientSecret.markAsUntouched();
	}

	async onRefreshStatus(): Promise<void>
	{
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
		this.spinnerService.showSpinner();

		this.showConsentStatus = false;
		this.showFormError = false;
		this.mismatchError = false;

		let product = this.data.product;
		this.monitorList = [];

		return product.service.getAllMonitorData(this.data.account, this.msGraphConnectionData).then( async monitorData => { 
			if ( monitorData != null )
			{
				this.monitorList = monitorData;
			}
			this.determineMismatchError();
			
			// now getting the data from the WF
			// should we bother if the feature is not enabled - TBD?
			// to get the most up to date I guess we should clear the cache first? Is this overkill?
			this.msteamsSharedService.clearMonitorWorkflowStatus();
			await this.updateDataAndStatusUsingWorkFlow(true);
			
			this.validateMonitorNumbers();
			this.spinnerService.hideSpinner();
		})
		.catch (err => {
			this.displayErrorBasedOnException( err, false );
			this.spinnerService.hideSpinner();
		})
	}

	onClose() 
	{
		this.clearAllErrorMessages();
		this.mismatchError = false;

		let result = undefined;
		this.msteamsSharedService.clearAllMSTeamsToCLPresenceCache(); // force re-request because monitors can change outside of this dialog
		// no waiting for this one to complete
		this.data.product.service.invokeWorkflowToRefreshSubscription(this.data.account);

		if ( this.monitorListChanged == true )
		{
			result = {
				setupComplete: true,
			}
		}

		this.dialogRef.close(result);
	}

	displayErrorBasedOnException( err: any, onCreateMonitor: boolean ) {
		let theMessage = '';
		console.error(`${logContext}.displayErrorBasedOnException error to display to user`, err);
		if ( onCreateMonitor == true ) 
			this.addErrorMsg = this.translateService.instant('integrations.MSTeamsToCLPresence.error-create-general');
		else // TBD
			this.formErrorMsg = this.translateService.instant('integrations.generic-connector-dialog.error-retrieving-generic-connector-config');

		this.alertType = FormAlertType.ERROR;

	 	if ( err.body && err.body.message ) {
			theMessage = err.body.message;
		}
		else if ( err.error && err.error.message ) {
			theMessage = err.error.message;
		}
		else // just show the generic message
		{
			if ( onCreateMonitor == true )
				this.showAddError = true;
			else
				this.showFormError = true;
			return;
		}

		// concatenate the generic and specific message
		if ( onCreateMonitor == true )
		{
			if ( theMessage != undefined || theMessage.length > 0 )
				this.addErrorMsg = this.addErrorMsg + ": " + theMessage;
			this.showAddError = true;
		}
		else
		{
			if ( theMessage != undefined || theMessage.length > 0 )
				this.formErrorMsg = this.formErrorMsg + ": " + theMessage;
			this.showFormError = true;
		}
	}

	private createOauthWindow(width = 500, height = 600, left = 0, top = 0): Window {
        let newWindow = window.open('about:blank', '_blank', `width=${width},height=${height},left=${left},top=${top}`);
        newWindow.addEventListener('message', event => {
            console.debug(`${logContext}.createOauthWindow message: `, event);
        });
        return newWindow;
    }

	handleConsentMessage(event)
	{
		console.log(`${logContext}.handleConsentMessage, event received`, event);
		if ( event.granted == "true" )
		{
			window.clearInterval(this.intervalId);
			this.consentMsg = this.translateService.instant('integrations.MSTeamsToCLPresence.consent-granted', {monitor: this.connectionData.monitorId});
			this.consentStatusType = FormAlertType.SUCCESS;
			this.showConsentStatus = true;

			let monitorIdx = this.monitorList.findIndex(m => m.connectionId == event.connectionId);
			this.monitorList[monitorIdx].status = PresenceMonitorState.AUTHORIZED;
			this.data.product.service.notifyWorkflowOfStatusChange( this.data.account );
			this.validateMonitorNumbers();
		}
		else if ( event.granted == "false" )
		{
			window.clearInterval(this.intervalId);
			this.consentMsg = this.translateService.instant('integrations.MSTeamsToCLPresence.consent-failed', {monitor: this.connectionData.monitorId});
			if ( event && event.error )
			{
				if ( event.error.indexOf("consent_required") != -1 )
					this.consentMsg = this.consentMsg + "  " + this.translateService.instant('integrations.MSTeamsToCLPresence.consent-required');
				else if ( event.error.indexOf("invalid_client") != -1 )
					this.consentMsg = this.consentMsg + "  " + this.translateService.instant('integrations.MSTeamsToCLPresence.invalid-client-secret');
				else
				{
					let tempError = decodeURIComponent(event.error);
					this.consentMsg = this.consentMsg + " " + this.translateService.instant('integrations.MSTeamsToCLPresence.consent-error-reason') + " " + tempError; // this might be technical but if we get some we can translated to a string
				}
			}
			this.consentStatusType = FormAlertType.ERROR;
			this.showConsentStatus = true;
			this.validateMonitorNumbers();
		}
		else // it never got to the consent part?
		{
			this.consentMsg = this.translateService.instant('integrations.MSTeamsToCLPresence.consent-error', {monitor: this.connectionData.monitorId});
			this.consentStatusType = FormAlertType.ERROR;
			this.showConsentStatus = true;
		}
	}

	clearAllErrorMessages()
	{
		this.showAddError = false;
		this.showFormError = false;
		this.showConsentStatus = false;
	}

	ngOnDestroy() {
		if (this.subscription) {
		  this.subscription.unsubscribe();
		}
	}
}
